import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Loader, btn } from "@/components/Page";

export default function LineupBuilder({ war, myAlliance, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [fighters, setFighters] = useState([]);
  const [lineup, setLineup] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [currentLineup, setCurrentLineup] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [fightersRes, lineupRes] = await Promise.all([
        api.get("/alliance-wars/eligible-fighters"),
        api.get("/alliance-wars/lineup")
      ]);
      
      setFighters(fightersRes.data.fighters || []);
      setCurrentLineup(lineupRes.data.submitted ? lineupRes.data : null);
      
      // Initialize lineup from current submission or empty
      if (lineupRes.data.submitted && lineupRes.data.rounds) {
        const initialLineup = {};
        for (let i = 1; i <= 7; i++) {
          const round = lineupRes.data.rounds[i.toString()];
          if (round && round.fighters) {
            initialLineup[i] = round.fighters.map(f => f.gladiator_id);
          } else {
            initialLineup[i] = [null, null];
          }
        }
        setLineup(initialLineup);
      } else {
        const emptyLineup = {};
        for (let i = 1; i <= 7; i++) {
          emptyLineup[i] = [null, null];
        }
        setLineup(emptyLineup);
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  
  const setFighter = (round, slot, fighterId) => {
    setLineup(prev => ({
      ...prev,
      [round]: [
        slot === 0 ? fighterId : prev[round][0],
        slot === 1 ? fighterId : prev[round][1]
      ]
    }));
  };
  
  const isUsed = (fighterId) => {
    for (let round = 1; round <= 7; round++) {
      if (lineup[round]?.includes(fighterId)) return true;
    }
    return false;
  };
  
  // Count how many fighters each owner has in the current lineup
  const getOwnerCounts = () => {
    const counts = {};
    for (let round = 1; round <= 7; round++) {
      const roundFighters = lineup[round] || [];
      for (const fighterId of roundFighters) {
        if (fighterId) {
          const fighter = fighters.find(f => f.gladiator_id === fighterId);
          if (fighter) {
            counts[fighter.owner_id] = (counts[fighter.owner_id] || 0) + 1;
          }
        }
      }
    }
    return counts;
  };
  
  const getAvailableFighters = () => {
    const ownerCounts = getOwnerCounts();
    return fighters.filter(f => {
      // Filter out fighters already used
      if (isUsed(f.gladiator_id)) return false;
      // Filter out fighters whose owner already has 3 selected
      if ((ownerCounts[f.owner_id] || 0) >= 3) return false;
      return true;
    });
  };
  
  const isComplete = () => {
    for (let round = 1; round <= 7; round++) {
      const roundFighters = lineup[round] || [];
      if (roundFighters[0] === null || roundFighters[1] === null) return false;
      if (roundFighters[0] === roundFighters[1]) return false;
    }
    return true;
  };
  
  const submitLineup = async () => {
    if (!isComplete()) {
      toast.error("Please select 2 unique fighters for each round");
      return;
    }
    
    setSubmitting(true);
    try {
      const rounds = {};
      for (let i = 1; i <= 7; i++) {
        rounds[i.toString()] = lineup[i].filter(f => f !== null);
      }
      
      await api.post("/alliance-wars/lineup", { rounds });
      toast.success("Lineup submitted successfully!");
      await loadData();
      onUpdate();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) return <Loader label="Loading fighters…" />;
  
  const usedCount = Object.values(lineup).flat().filter(f => f !== null).length;
  const complete = isComplete();
  const locked = currentLineup?.locked;
  const ownerCounts = getOwnerCounts();
  
  return (
    <div className="space-y-6">
      {/* Status Header */}
      <div className="border border-amber-900/30 bg-zinc-950/50 p-5 rounded-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-cinzel text-base text-amber-300 uppercase tracking-wide">
              {locked ? "🔒 Lineup Locked" : "Build Your Lineup"}
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-manrope">
              Select 2 unique fighters for each of the 7 rounds · <span className="text-amber-400">Max 3 fighters per lanista</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="font-cinzel text-xl text-amber-400">{usedCount}/14</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-manrope">fighters</div>
            </div>
            
            {complete && !locked && (
              <button
                onClick={submitLineup}
                disabled={submitting}
                className={btn("primary")}
              >
                {submitting ? "Submitting..." : currentLineup?.submitted ? "Update Lineup" : "Submit Lineup"}
              </button>
            )}
          </div>
        </div>
        
        {!complete && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-400/90 border-t border-amber-900/20 pt-3">
            <span className="font-manrope">⚠️ You must select all 14 fighters (2 per round) before submitting</span>
          </div>
        )}
      </div>
      
      {/* Lineup Grid */}
      <div className="space-y-3">
        {[1, 2, 3, 4, 5, 6, 7].map(round => {
          const day = round + 3;
          const roundComplete = lineup[round]?.[0] && lineup[round]?.[1] && lineup[round][0] !== lineup[round][1];
          
          return (
            <div
              key={round}
              className={`border p-4 rounded-sm ${
                roundComplete ? "border-green-900/50 bg-green-950/10" : "border-zinc-800 bg-zinc-950/50"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {roundComplete && <span className="text-green-500 text-lg">✓</span>}
                  <div>
                    <div className="font-cinzel text-sm text-amber-400 uppercase tracking-wide">Round {round}</div>
                    <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-manrope">Day {day} • 5:00 PM</div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[0, 1].map(slot => (
                  <FighterSlot
                    key={slot}
                    round={round}
                    slot={slot}
                    selectedId={lineup[round]?.[slot]}
                    availableFighters={getAvailableFighters()}
                    allFighters={fighters}
                    onSelect={(fighterId) => setFighter(round, slot, fighterId)}
                    locked={locked}
                    ownerCounts={ownerCounts}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Submit Button (Bottom) - For Convenience */}
      {complete && !locked && (
        <div className="flex justify-center py-4">
          <button
            onClick={submitLineup}
            disabled={submitting}
            className={btn("primary") + " w-full sm:w-auto"}
          >
            {submitting ? "Submitting..." : currentLineup?.submitted ? "Update Lineup" : "Submit Lineup"}
          </button>
        </div>
      )}
      
      {/* Available Fighters - Enhanced */}
      <AvailableFightersPanel 
        fighters={getAvailableFighters()} 
        onSelectFighter={(fighterId) => {
          // Find first empty slot to place the fighter
          for (let round = 1; round <= 7; round++) {
            if (lineup[round][0] === null) {
              setFighter(round, 0, fighterId);
              return;
            }
            if (lineup[round][1] === null) {
              setFighter(round, 1, fighterId);
              return;
            }
          }
        }}
        locked={locked}
      />
    </div>
  );
}

function AvailableFightersPanel({ fighters, onSelectFighter, locked }) {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('rating'); // 'rating' or 'name'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' or 'desc'
  const perPage = 20;
  
  // Sort fighters
  const sortedFighters = [...fighters].sort((a, b) => {
    let aVal = sortBy === 'rating' ? a.rating : a.name.toLowerCase();
    let bVal = sortBy === 'rating' ? b.rating : b.name.toLowerCase();
    
    const compare = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === 'asc' ? compare : -compare;
  });
  
  // Paginate
  const totalPages = Math.ceil(sortedFighters.length / perPage);
  const startIdx = (page - 1) * perPage;
  const endIdx = startIdx + perPage;
  const displayedFighters = sortedFighters.slice(startIdx, endIdx);
  
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir(field === 'rating' ? 'desc' : 'asc'); // Default to desc for rating, asc for name
    }
  };
  
  return (
    <div className="border border-zinc-800 bg-zinc-950/50 rounded-sm">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-cinzel uppercase tracking-wide text-zinc-400">
            Available Fighters
          </div>
          <div className="text-[10px] text-zinc-400 mt-0.5">
            {fighters.length} fighter{fighters.length !== 1 ? 's' : ''} available · Click to add to first empty slot
          </div>
        </div>
        
        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-zinc-600 uppercase tracking-wider">Sort by:</div>
          <button
            onClick={() => toggleSort('rating')}
            className={`text-[10px] px-2 py-1 border rounded-sm uppercase tracking-wide transition-colors ${
              sortBy === 'rating' 
                ? 'border-amber-600 bg-amber-950/30 text-amber-400' 
                : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Rating {sortBy === 'rating' && (sortDir === 'desc' ? '↓' : '↑')}
          </button>
          <button
            onClick={() => toggleSort('name')}
            className={`text-[10px] px-2 py-1 border rounded-sm uppercase tracking-wide transition-colors ${
              sortBy === 'name' 
                ? 'border-amber-600 bg-amber-950/30 text-amber-400' 
                : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'
            }`}
          >
            Name {sortBy === 'name' && (sortDir === 'desc' ? '↓' : '↑')}
          </button>
        </div>
      </div>
      
      {/* Fighters List */}
      {fighters.length === 0 ? (
        <div className="p-8 text-center text-zinc-600 text-sm">
          No available fighters. All eligible fighters have been selected or their owners have reached the 3-fighter limit.
        </div>
      ) : (
        <>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {displayedFighters.map(f => (
                <button
                  key={f.gladiator_id}
                  onClick={() => !locked && onSelectFighter(f.gladiator_id)}
                  disabled={locked}
                  className={`text-left p-3 border rounded-sm transition-all ${
                    locked
                      ? 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                      : 'border-zinc-800 bg-zinc-900 hover:border-amber-600 hover:bg-zinc-800 text-zinc-300 cursor-pointer'
                  }`}
                >
                  <div className="text-sm font-medium text-amber-400 mb-1 truncate">
                    {f.name}
                  </div>
                  <div className="text-[10px] text-zinc-400 space-y-0.5">
                    <div>Rating: <span className="text-zinc-300 font-mono">{f.rating}</span></div>
                    <div>Owner: <span className="text-zinc-300">{f.owner_name}</span></div>
                    <div className="text-zinc-500">Record: {f.record}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
              <div className="text-[10px] text-zinc-600">
                Showing {startIdx + 1}-{Math.min(endIdx, fighters.length)} of {fighters.length}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="text-[10px] px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-600 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-wide"
                >
                  Previous
                </button>
                
                <div className="text-[10px] text-zinc-500 px-2">
                  Page {page} of {totalPages}
                </div>
                
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="text-[10px] px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-600 rounded-sm disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-wide"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FighterSlot({ round, slot, selectedId, availableFighters, allFighters, onSelect, locked, ownerCounts }) {
  const selected = allFighters.find(f => f.gladiator_id === selectedId);
  
  // When a fighter is selected, allow them in options even if their owner has 3
  // (so user can change selection from the same owner)
  const options = selectedId 
    ? [selected, ...availableFighters].filter(Boolean)
    : availableFighters;
  
  const selectedOwnerCount = selected ? (ownerCounts[selected.owner_id] || 0) : 0;
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 p-3">
      <div className="text-[10px] text-zinc-400 uppercase tracking-widest mb-2">Fighter {slot + 1}</div>
      
      {locked ? (
        <div className="flex items-center gap-2">
          <div>
            <div className="text-sm text-amber-400 font-semibold">{selected?.name || "Not Selected"}</div>
            {selected && (
              <div className="text-[10px] text-zinc-600">
                Rating: {selected.rating} • {selected.owner_name}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <select
            value={selectedId || ""}
            onChange={(e) => onSelect(e.target.value || null)}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-zinc-300 focus:border-amber-600 focus:outline-none"
          >
            <option value="">Select fighter...</option>
            {options.map(f => (
              <option key={f.gladiator_id} value={f.gladiator_id}>
                {f.name} ({f.rating}) - {f.owner_name}
              </option>
            ))}
          </select>
          {selected && selectedOwnerCount >= 2 && (
            <div className="text-[10px] text-amber-500 mt-1">
              {selected.owner_name}: {selectedOwnerCount}/3 fighters
            </div>
          )}
        </>
      )}
    </div>
  );
}
